import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { inject, Signal } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { EMPTY, from, pipe, tap, switchMap, of, map, filter } from "rxjs";
import { withReqState, setPropError, setPropLoading, setPropLoaded, setPropInit } from "@application/data-access";
import { ProfileService } from "./infra/profile.service";
import { TActivity, TComment, TProfile, TResponsible, TPhone, TAddress, EPersonIncludeType, ERegistrationType, TCommentToggleRead, TTimelineEvent, TGraduationList, TGraduation, TSimpleLoginCode, TPersonIntegration, TPaguelaAccount } from "./entities/profile.model";
import { IVerifyEmail, UserService } from '@person/user/data-access';
import { AlertStore } from "@alert/data-access";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorStore } from "@application/errors/data-access";
import { IFile } from "@shared/filegroup/data-access";
import { APP_CONFIG } from "@application/config";
import { DataSource } from 'devextreme/common/data';
import { PermissionNumber, SelfUserStore } from '@person/user/self/data-access';
import { DateTime } from 'luxon';
import { DialogStore } from '@shared/components/ui/dialog/data-access';
import { TCategory, TCategoryHistory } from '@settings/data-access';
import { CreditAssessmentPF, CreditAssessmentPFAll, CreditAssessmentPJ, CreditAssessmentPJAll } from './entities/credit-assessment.model';
import { TPersonProduct } from '@consumption/data-access';
import { RouterStore } from '@route/data-access';
import { EPaguelaSituation } from '@financial/data-access';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

type TPermissionAction = 'create' | 'update' | 'view' | 'delete';

interface IPermissionRequestConfig {
  permissions: number[];
  translationKey: string;
}

export interface IProfileStore {
  profile: TProfile;
  profileEdit: TProfile;
  profileActivities: TActivity[];
  registrationType: ERegistrationType;
  isNewProfile: boolean;
  personId?: string;
  responsible?: TResponsible;
  lastComment?: TComment;
  activities?: TActivity[];
  showProfileEditModal?: boolean;
  actionOnSaveProfileModal?: (personId: string) => void;
  showViewCommentModal?: boolean;
  showEditCommentModal?: boolean;
  showGroupedUnitiesModal?: boolean;
  openComment?: TComment;
  toggleRead?: TCommentToggleRead;
  documentShowEditModal: boolean;
  documentShowViewModal: boolean;
  documentShowAddModal: boolean;
  documentFile: IFile;
  receivementSummary: number;
  accountsReceivablesSummary: number;
  openMonthByPerson?: number;
  simpleLoginCode: TSimpleLoginCode;
  contractInfo: string;
  VerifyEmailState: Record<string, 'init' | 'loading' | 'loaded' | 'error'>;
  VerifyEmailResponse: IVerifyEmail;
  ReVerifyEmailState: Record<string, 'init' | 'loading' | 'loaded' | 'error'>;
  ReVerifyEmailResponse: IVerifyEmail;
  email: {
    isValid?: boolean;
    exists?: boolean;
  };
  /** True when temporary password is blocked for the current form email (API returned false from Verification/IsPossibleToSetTemporaryEmail). Null while loading. */
  canSetTemporaryEmail: boolean | null;
  accessCd: {
    exists?: boolean;
  };
  cpf: {
    exists?: boolean;
    code?: string;
  };
  fullName: {
    exists?: boolean;
    code?: string;
  };
  company: {
    fantasyName?: string;
    legalBusinessName?: string;
  },
  AccessFrequency: TAccessLog[];
  phones?: TPhone[];
  addresses?: TAddress[];
  TimelineNextDate: string;
  TimelineEvents: TTimelineEvent[];
  graduation: Partial<TGraduation>;
  graduations?: TGraduationList[];
  showGraduationModal: boolean;
  personCategoryHistory?: TCategoryHistory[];
  personCategory?: TCategory;
  allCreditAssessmentPJ?: CreditAssessmentPJAll[];
  creditAssessmentPJ?: CreditAssessmentPJ;
  allCreditAssessmentPF?: CreditAssessmentPFAll[];
  creditAssessmentPF?: CreditAssessmentPF;
  integration?: TPersonIntegration;
  profileProducts: TPersonProduct[];
  paguelaAccount?: TPaguelaAccount;
}

export type TAccessLog = {
  AccessOnUtc: string;
  AccessLogId: string;
}

export const initialProfileStore: IProfileStore = {
  profile: null,
  profileEdit: null,
  profileActivities: null,
  personId: null,
  registrationType: null,
  isNewProfile: false,
  responsible: null,
  lastComment: null,
  activities: null,
  showProfileEditModal: false,
  actionOnSaveProfileModal: null,
  showViewCommentModal: false,
  showEditCommentModal: false,
  showGroupedUnitiesModal: false,
  openComment: null,
  toggleRead: null,
  documentShowEditModal: false,
  documentShowViewModal: false,
  documentShowAddModal: false,
  documentFile: null,
  receivementSummary: null,
  accountsReceivablesSummary: null,
  openMonthByPerson: null,
  contractInfo: null,
  simpleLoginCode: null,
  VerifyEmailState: {},
  VerifyEmailResponse: null,
  ReVerifyEmailState: {},
  ReVerifyEmailResponse: null,
  email: {
    isValid: false,
    exists: false,
  },
  canSetTemporaryEmail: null,
  accessCd: {
    exists: false,
  },
  cpf: {
    exists: false,
  },
  fullName: {
    exists: false,
  },
  company: {
    fantasyName: null,
    legalBusinessName: null,
  },
  phones: [],
  addresses: [],
  AccessFrequency: [],
  TimelineNextDate: null,
  TimelineEvents: [],
  graduations: [],
  graduation: null,
  showGraduationModal: false,
  personCategoryHistory: null,
  personCategory: null,
  allCreditAssessmentPJ: null,
  creditAssessmentPJ: null,
  allCreditAssessmentPF: null,
  creditAssessmentPF: null,
  integration: null,
  profileProducts: null,
  paguelaAccount: null,
};

export const ProfileStore = signalStore(
  { providedIn: 'root' },
  withState<IProfileStore>(initialProfileStore),
  withMethods((
    store,
    profileService = inject(ProfileService),
    routerStore = inject(RouterStore),
    userService = inject(UserService),
    alertStore = inject(AlertStore),
    errorStore = inject(ErrorStore),
    dxOdataStoreService = inject(DxOdataStoreService),
    selfUserStore = inject(SelfUserStore),
    url = inject(APP_CONFIG).apiUrl,
    dialogStore = inject(DialogStore)
  ) => {

    const resolveRegistrationType = (registrationType?: ERegistrationType | null): ERegistrationType | null => {
      if (registrationType !== null && registrationType !== undefined) {
        return registrationType;
      }

      const storeRegistrationType = store.registrationType?.();
      if (storeRegistrationType !== null && storeRegistrationType !== undefined) {
        return storeRegistrationType;
      }

      const currentProfile = store.profile?.();
      if (currentProfile?.RegistrationType !== null && currentProfile?.RegistrationType !== undefined) {
        return currentProfile.RegistrationType;
      }

      const currentProfileEdit = store.profileEdit?.();
      if (currentProfileEdit?.RegistrationType !== null && currentProfileEdit?.RegistrationType !== undefined) {
        return currentProfileEdit.RegistrationType;
      }

      return null;
    };

    const buildPermissionRequests = (
      action: TPermissionAction,
      registrationType?: ERegistrationType | null,
    ): IPermissionRequestConfig[] => {
      const resolvedType = resolveRegistrationType(registrationType);

      const customerPermissions: Record<TPermissionAction, IPermissionRequestConfig> = {
        create: { permissions: [PermissionNumber.ClienteAdicionar], translationKey: 'CUSTOMER_CREATE' },
        update: { permissions: [PermissionNumber.ClienteAtualizar], translationKey: 'CUSTOMER_UPDATE' },
        view: { permissions: [PermissionNumber.ClienteObterPorId], translationKey: 'CUSTOMER_VIEW' },
        delete: { permissions: [PermissionNumber.ClienteRemover], translationKey: 'CUSTOMER_DELETE' },
      };

      const contactPermissions: Record<TPermissionAction, IPermissionRequestConfig> = {
        create: { permissions: [PermissionNumber.ContatoAdicionar], translationKey: 'CONTACT_CREATE' },
        update: { permissions: [PermissionNumber.ContatoAtualizar], translationKey: 'CONTACT_UPDATE' },
        view: { permissions: [PermissionNumber.ContatoObterPorId], translationKey: 'CONTACT_VIEW' },
        delete: { permissions: [PermissionNumber.ContatoRemover], translationKey: 'CONTACT_DELETE' },
      };

      const visitorPermissions: Record<TPermissionAction, IPermissionRequestConfig> = {
        create: { permissions: [PermissionNumber.VisitanteAdicionar], translationKey: 'VISITOR_CREATE' },
        update: { permissions: [PermissionNumber.VisitanteAtualizar], translationKey: 'VISITOR_UPDATE' },
        view: { permissions: [PermissionNumber.VisitanteObterPorId], translationKey: 'VISITOR_VIEW' },
        delete: { permissions: [PermissionNumber.VisitanteRemover], translationKey: 'VISITOR_DELETE' },
      };

      const requests: IPermissionRequestConfig[] = [];

      const pushRequest = (config?: IPermissionRequestConfig) => {
        if (config) {
          requests.push(config);
        }
      };

      switch (resolvedType) {
        case ERegistrationType.Customer:
          pushRequest(customerPermissions[action]);
          break;
        case ERegistrationType.Contact:
          pushRequest(contactPermissions[action]);
          break;
        case ERegistrationType.Visitor:
          pushRequest(visitorPermissions[action]);
          break;
        default:
          pushRequest(customerPermissions[action]);
          pushRequest(contactPermissions[action]);
          pushRequest(visitorPermissions[action]);
          break;
      }

      return requests;
    };

    const ensurePermissionForAction = async (
      action: TPermissionAction,
      registrationType?: ERegistrationType | null,
    ): Promise<boolean> => {
      const requests = buildPermissionRequests(action, registrationType);

      if (!requests.length) {
        return true;
      }

      for (const request of requests) {
        const hasPermission = await selfUserStore.ensurePermissions(request.permissions, { interactive: false });
        if (hasPermission) {
          return true;
        }
      }

      for (const request of requests) {
        const hasPermission = await selfUserStore.ensurePermissions(request.permissions, {
          title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${request.translationKey}.TITLE`,
          message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${request.translationKey}.MESSAGE`,
        });

        if (hasPermission) {
          return true;
        }
      }

      return false;
    };

    const deleteGraduationById = rxMethod<string>(pipe(
      tap(() => patchState(store, { ...setPropLoading('deleteGraduationById') })),
      switchMap((id) =>
        profileService.deleteGraduationById(id).pipe(
          tapResponse({
            next: () => {
              patchState(store, { ...setPropLoaded('deleteGraduationById') });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, { ...setPropError('deleteGraduationById', error) });
            }
          })
        )
      )
    ));

    return {
      clearPersonData: () => patchState(store, initialProfileStore),
      blankState: () => {
        patchState(store, { ...setPropInit('getProfile') });
      },

      blankCommentState: () => {
        patchState(store, {
          openComment: null, ...setPropInit('addComment', 'updateComment', 'removeComment', 'getComment')
        })
      },

      blankGraduationState: () => {
        patchState(store, { graduation: null, graduations: null }, setPropInit('deleteGraduationById', 'getAllGraduationsByPersonId', 'createGraduation', 'updateGraduation'));
      },

      setPersonId: (personId: string) => {
        patchState(store, { personId });
      },

      setProfileEdit: (profileEdit: TProfile) => {
        patchState(store, { profileEdit });
      },

      toggleProfileEditModal: (showProfileEditModal = !store.showProfileEditModal(), actionOnSaveProfileModal: (personId: string) => void = null) => {
        patchState(store, { showProfileEditModal, actionOnSaveProfileModal });
      },

      toggleViewCommentModal: (showViewCommentModal = !store.showViewCommentModal()) => {
        patchState(store, { showViewCommentModal });
      },

      toggleEditCommentModal: (showEditCommentModal = !store.showEditCommentModal()) => {
        patchState(store, { showEditCommentModal });
      },

      toggleGroupedUnitiesModal: (showGroupedUnitiesModal = !store.showGroupedUnitiesModal()) => {
        patchState(store, { showGroupedUnitiesModal });
      },

      setOpenComment: (openComment: TComment) => {
        patchState(store, { openComment });
      },

      setRegistrationType: (registrationType: ERegistrationType) => {
        patchState(store, { registrationType });
      },

      setIsNewProfile: (isNewProfile: boolean) => {
        patchState(store, { isNewProfile });
      },

      blankStateEditModal: () => {
        patchState(store, {
          profileEdit: null, ...setPropInit(
            'createPerson',
            'updatePerson',
            'profileEdit'
          )
        });
      },

      updateDocumentConfig: (documentConfig: { documentShowEditModal?: boolean; documentShowViewModal?: boolean; documentShowAddModal?: boolean; documentFile?: IFile }) => {
        patchState(store, documentConfig);
      },

      verifyEmail: rxMethod<{ Email: string, StateKey?: string }>(
        pipe(
          tap(({ StateKey }) => patchState(store, { ...setPropLoading('verifyEmail'), VerifyEmailState: { ...store.VerifyEmailState(), [StateKey]: 'loading' } })),
          switchMap(({ Email, StateKey }) =>
            userService.verifyEmail(Email).pipe(
              tapResponse({
                next: (response) => {
                  if (StateKey) {
                    patchState(store, { VerifyEmailResponse: response, VerifyEmailState: { ...store.VerifyEmailState(), [StateKey]: 'loaded' },  ...setPropLoaded('verifyEmail') });
                    return;
                  }
                  patchState(store, { email: { isValid: response?.IsValid }, ...setPropLoaded('verifyEmail') });
                },
                error: (e: HttpErrorResponse) => {
                  patchState(store, { VerifyEmailState: { ...store.VerifyEmailState(), [StateKey]: 'error' }, ...setPropError('verifyEmail', e) });
                },
              })
            )
          )
        )
      ),

      reVerifyEmail: rxMethod<{ Email: string, StateKey?: string }>(
        pipe(
          tap(({ StateKey }) => patchState(store, { ...setPropLoading('reVerifyEmail'), ReVerifyEmailState: { ...store.ReVerifyEmailState(), [StateKey]: 'loading' } })),
          switchMap(({ Email, StateKey }) =>
            userService.reVerifyEmail(Email).pipe(
              tapResponse({
                next: (response) => {
                  if (response.IsValid) {
                    alertStore.create({ message: 'PROFILE.MESSAGES.EMAIL_VERIFIED_SUCCESS', title: 'COMPONENTS.ALERT.SUCCESS', type: 'success' });
                  } else {
                    alertStore.create({ message: 'PROFILE.MESSAGES.EMAIL_VERIFIED_UNVALID', title: 'COMPONENTS.ALERT.ATTENTION', type: 'warning' });
                  }
                  if (StateKey) {
                    patchState(store, { ReVerifyEmailResponse: response, ReVerifyEmailState: { ...store.ReVerifyEmailState(), [StateKey]: 'loaded' } });
                    return;
                  }
                  patchState(store, { email: { isValid: response?.IsValid }, ...setPropLoaded('reVerifyEmail') });
                },
                error: (e: HttpErrorResponse) => {
                  patchState(store, { ReVerifyEmailState: { ...store.ReVerifyEmailState(), [StateKey]: 'error' }, ...setPropError('reVerifyEmail', e) });
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      isEmailAlreadyUsed: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('isEmailAlreadyUsed') })),
          switchMap((email) =>
            userService.isEmailAlreadyUsed(email).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { email: { ...store.email(), exists: response }, ...setPropLoaded('isEmailAlreadyUsed') });
                },
                error: (error) => {
                  patchState(store, { ...setPropError('isEmailAlreadyUsed', error) });
                },
              })
            )
          )
        )
      ),

      checkIsPossibleToSetTemporaryEmail: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('checkIsPossibleToSetTemporaryEmail'), canSetTemporaryEmail: null })),
          switchMap((email) =>
            userService.isPossibleToSetTemporaryEmail(email).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { canSetTemporaryEmail: !!response, ...setPropLoaded('checkIsPossibleToSetTemporaryEmail') });
                },
                error: (error) => {
                  patchState(store, { canSetTemporaryEmail: false, ...setPropError('checkIsPossibleToSetTemporaryEmail', error) });
                },
              })
            )
          )
        )
      ),

      loadProfile: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('getProfile') })),
          switchMap(() =>
            from(ensurePermissionForAction('view')).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, { ...setPropInit('getProfile') });
                  return EMPTY;
                }

                const includes = [
                  EPersonIncludeType.Phones,
                  EPersonIncludeType.Addresses,
                  EPersonIncludeType.Responsible,
                  EPersonIncludeType.UserConsolidated,
                  EPersonIncludeType.Preference,
                  EPersonIncludeType.Access,
                  EPersonIncludeType.HealthConsolidated,
                  EPersonIncludeType.HistoryConsolidated,
                  EPersonIncludeType.EventConsolidated,
                  EPersonIncludeType.Integration
                ];

                if (selfUserStore.isAdminScope()) {
                  includes.push(EPersonIncludeType.Tax);
                }

                return profileService.getPersonWithSpecificIncludes(store.personId(), includes).pipe(
                  tapResponse({
                    next: (profile) => {
                      patchState(store, { profile, registrationType: profile?.RegistrationType ?? resolveRegistrationType(), ...setPropLoaded('getProfile') });
                    },
                    error: (error) => {
                      patchState(store, { ...setPropError('getProfile', error) });
                    },
                  })
                );
              })
            )
          )
        )
      ),

      loadProfileEdit: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('profileEdit') })),
          switchMap((personId) =>
            from(ensurePermissionForAction('view')).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, { ...setPropInit('profileEdit') });
                  return EMPTY;
                }

                const includes = [
                  EPersonIncludeType.Phones,
                  EPersonIncludeType.Addresses,
                  EPersonIncludeType.Responsible,
                  EPersonIncludeType.UserConsolidated,
                  EPersonIncludeType.Preference,
                  EPersonIncludeType.Access,
                  EPersonIncludeType.HealthConsolidated,
                  EPersonIncludeType.HistoryConsolidated,
                  EPersonIncludeType.EventConsolidated,
                ];

                if (selfUserStore.isAdminScope()) {
                  includes.push(EPersonIncludeType.Tax);
                }

                return profileService.getPersonWithSpecificIncludes(personId, includes).pipe(
                  tapResponse({
                    next: (profileEdit) => {
                      patchState(store, { profileEdit, registrationType: profileEdit?.RegistrationType ?? resolveRegistrationType(), ...setPropLoaded('profileEdit') });
                    },
                    error: (error) => {
                      patchState(store, { ...setPropError('profileEdit', error) });
                    },
                  })
                );
              })
            )
          )
        )
      ),

      getCommentsDataSource: (): DataSource => {
        return dxOdataStoreService.fromOdata({
          url: `${url.odata_person_service}`,
          key: 'CommentId',
          metadata: 'Comments',
          params: {
            $filter: `TargetPersonId eq ${store.personId()}`,
            $select: 'Title,Read,UpdatedOn,AddedOn,CommentId,AddedBy,UpdatedBy,SourceName',
          }
        });
      },

      getLastComment: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('getLastComment') })),
          switchMap((personId) =>
            profileService.getLastComment(personId).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { lastComment: response?.value?.[0] ?? null, ...setPropLoaded('getLastComment') });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { lastComment: null, ...setPropError('getLastComment', error) });
                },
              })
            )
          )
        )
      ),

      getComment: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('getComment') })),
          switchMap((commentId) =>
            profileService.getComment(commentId).pipe(
              tapResponse({
                next: (openComment) => {
                  patchState(store, { openComment, ...setPropLoaded('getComment') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('getComment', error) });
                },
              })
            )
          )
        )
      ),

      toggleReadComment: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('toggleRead') })),
          switchMap((commentId) =>
            profileService.toggleReadComment(commentId).pipe(
              tapResponse({
                next: (toggleRead) => {
                  patchState(store, { toggleRead, ...setPropLoaded('toggleRead') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('toggleRead', error) });
                },
              })
            )
          )
        )
      ),

      checkAccessCdExists: rxMethod<number>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('checkAccessCdExists') })),
          switchMap((cd) =>
            profileService.checkAccessCdExists(cd).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { accessCd: { exists: response }, ...setPropLoaded('checkAccessCdExists') });
                },
                error: (error) => {
                  patchState(store, { ...setPropError('checkAccessCdExists', error) });
                },
              })
            )
          )
        )
      ),

      checkCpfExists: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('checkCpfExists') })),
          switchMap((cpf) =>
            profileService.searchPerson(cpf).pipe(
              tapResponse({
                next: (response) => {
                 const value = response?.results;
                  const exists = value?.length > 0 && cpf === value?.[0]?.fiscal_identity;
                  const code = value?.[0]?.friendly_code;
                  
                  if (code !== store?.profileEdit()?.FriendlyCode) {
                    patchState(store, { cpf: { exists, code }, ...setPropLoaded('checkCpfExists') });
                  }
                },
                error: (error) => {
                  patchState(store, { ...setPropError('checkCpfExists', error) });
                },
              })
            )
          )
        )
      ),

      checkFullNameExists: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('checkFullNameExists') })),
          switchMap((fullName) =>
            profileService.searchPerson(fullName).pipe(
              tapResponse({
                next: (response) => {
                  const value = response?.results;
                  const exists = value?.length > 0 && fullName.toLowerCase() === value?.[0]?.full_name?.toLowerCase();
                  const code = value?.[0]?.friendly_code;

                  if (code !== store?.profileEdit()?.FriendlyCode) {
                    patchState(store, { fullName: { exists, code }, ...setPropLoaded('checkFullNameExists') });
                  }
                },
                error: (error) => {
                  patchState(store, { ...setPropError('checkFullNameExists', error) });
                },
              })
            )
          )
        )
      ),

      checkCompany: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('checkCompany') })),
          switchMap((company) =>
            profileService.checkFiscalIdentity('cnpj', company).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { company: { fantasyName: response?.['fantasia'], legalBusinessName: response?.['nome'] }, ...setPropLoaded('checkCompany') });
                },
                error: (error) => {
                  patchState(store, { ...setPropError('checkCompany', error) });
                },
              })
            )
          )
        )
      ),

      createPerson: rxMethod<TProfile>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('createPerson') })),
          switchMap((payload) =>
            from(ensurePermissionForAction('create', payload?.RegistrationType)).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, { ...setPropInit('createPerson') });
                  return EMPTY;
                }

                return profileService.createPerson(payload).pipe(
                  tapResponse({
                    next: (profile) => {
                      alertStore.create({ message: 'PERSON.GENERAL.ADD_SUCCESSFULLY', type: 'success', title: 'ALERT.TITLE_SUCCESS' });
                      patchState(store, { profile, registrationType: profile?.RegistrationType ?? resolveRegistrationType(payload?.RegistrationType), ...setPropLoaded('createPerson') });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, { ...setPropError('createPerson', error) });
                    },
                  })
                );
              })
            )
          )
        )
      ),

      updatePerson: rxMethod<Partial<TProfile>>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updatePerson') })),
          switchMap((payload) =>
            from(ensurePermissionForAction('update', payload?.RegistrationType)).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, { ...setPropInit('updatePerson') });
                  return EMPTY;
                }

                const profile = store.profile();
                const updatedProfile = { ...profile, ...payload };

                delete updatedProfile?.FullDisplayName;
        
                return profileService.patchPersonWithIncludes(updatedProfile).pipe(
                  tapResponse({
                    next: (updatedProfileFromApi) => {
                      if (updatedProfileFromApi == null) {
                        patchState(store, { ...setPropInit('updatePerson') });
                        return;
                      }
                      alertStore.create({ message: 'PERSON.GENERAL.UPDATE_SUCCESSFULLY', type: 'success', title: 'ALERT.TITLE_SUCCESS' });
                      const currentRouteProfile = store.profile();
                      const payloadPersonId = payload.PersonId ?? updatedProfileFromApi.PersonId;
                      if (currentRouteProfile?.PersonId != null && payloadPersonId != null && payloadPersonId !== currentRouteProfile.PersonId) {
                        routerStore.navigate([`/profile/${payloadPersonId}`]);
                      }
                      patchState(store, {
                        profile: updatedProfileFromApi,
                        registrationType: updatedProfileFromApi.RegistrationType ?? resolveRegistrationType(),
                        ...setPropLoaded('updatePerson')
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, { ...setPropError('updatePerson', error) });
                    },
                  })
                );
              })
            )
          )
        )
      ),
      patchPerson: rxMethod<Partial<TProfile>>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('patchPerson') })),
          switchMap((propertyPatch) => {
            const patchPerson = {
              PersonId: store.personId(),
              ...propertyPatch
            };
            return profileService.patchPerson(patchPerson).pipe(
              tapResponse({
                next: (updatedProfile) => {
                  patchState(store, {
                    profile: updatedProfile,
                    ...setPropLoaded('patchPerson')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PERSON.GENERAL.UPDATE_ERROR' });
                  patchState(store, { ...setPropError('patchPerson', error) });
                },
              })
            );
          })
        )
      ),

      loadResponsible: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('getResponsible') })),
          switchMap(() =>
            profileService.getResponsible(store.personId()).pipe(
              tapResponse({
                next: (responsible) => {
                  patchState(store, { responsible, ...setPropLoaded('getResponsible') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('getResponsible', error) });
                },
              })
            )
          )
        )
      ),

      createResponsible: rxMethod<TResponsible>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('createResponsible') })),
          switchMap((responsible) => {
            const responsibleData = {
              ...responsible,
              PersonId: store.personId()
            };

            return profileService.createResponsible(responsibleData).pipe(
              tapResponse({
                next: (createdResponsible) => {
                  alertStore.create({
                    message: 'PROFILE.ALERT.SUCCESS.RESPONSIBLE',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });
                  patchState(store, {
                    responsible: createdResponsible,
                    ...setPropLoaded('createResponsible')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_ADD' });
                  patchState(store, { ...setPropError('createResponsible', error) });
                },
              })
            );
          })
        )
      ),

      updateResponsible: rxMethod<TResponsible>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updateResponsible') })),
          switchMap((responsible) => {
            const responsibleData = {
              ...responsible,
              PersonId: store.personId()
            };

            return profileService.updateResponsible(responsibleData).pipe(
              tapResponse({
                next: (updatedResponsible) => {
                  alertStore.create({
                    message: 'Responsável atualizado com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });
                  patchState(store, {
                    responsible: updatedResponsible,
                    ...setPropLoaded('updateResponsible')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_EDIT'});
                  patchState(store, { ...setPropError('updateResponsible', error) });
                },
              })
            );
          })
        )
      ),

      patchResponsible: rxMethod<Partial<TResponsible>>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('patchResponsible') })),
          switchMap((propertyPatch) => {
            const patchData = {
              PersonId: store.personId(),
              ...propertyPatch
            };

            return profileService.patchResponsible(patchData).pipe(
              tapResponse({
                next: (updatedResponsible) => {
                  patchState(store, {
                    responsible: updatedResponsible,
                    ...setPropLoaded('patchResponsible')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_EDIT'});
                  patchState(store, { ...setPropError('patchResponsible', error) });
                },
              })
            );
          })
        )
      ),

      deleteResponsible: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('deleteResponsible') })),
          switchMap(() =>
            profileService.deleteResponsible(store.personId()).pipe(
              tapResponse({
                next: () => {
                  alertStore.create({
                    message: 'Responsável removido com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });
                  patchState(store, {
                    responsible: null,
                    ...setPropLoaded('deleteResponsible')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ERROR.RESPONSIBLE_DELETE'});
                  patchState(store, { ...setPropError('deleteResponsible', error) });
                },
              })
            )
          )
        )
      ),

      addComment: rxMethod<TComment>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('addComment') })),
          switchMap((comment) =>
            profileService.adicionarComentario(comment).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { ...setPropLoaded('addComment') });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('addComment', error) });
                },
              })
            )
          )
        )
      ),

      updateComment: rxMethod<TComment>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updateComment') })),
          switchMap((comment) =>
            profileService.atualizarComentario(comment).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { ...setPropLoaded('updateComment') });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('updateComment', error) });
                },
              })
            )
          )
        )
      ),

      removeComment: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('removeComment') })),
          switchMap((id) =>
            profileService.removerComentario(id).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { ...setPropLoaded('removeComment') });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('removeComment', error) });
                },
              })
            )
          )
        )
      ),

      emailBlankState: () => {
        patchState(store, { email: { isValid: false, exists: false }, canSetTemporaryEmail: null, ...setPropInit('verifyEmail', 'isEmailAlreadyUsed', 'reVerifyEmail', 'checkIsPossibleToSetTemporaryEmail') });
      },


      loadPhones: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadPhones') })),
          switchMap(() =>
            profileService.getByPerson(store.personId()).pipe(
              tapResponse({
                next: (phones) => {
                  patchState(store, { phones, ...setPropLoaded('loadPhones') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONES_LOAD' });
                  patchState(store, { ...setPropError('loadPhones', error) });
                },
              })
            )
          )
        )
      ),

      loadPhone: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadPhone') })),
          switchMap((id) =>
            profileService.getPhone(id).pipe(
              tapResponse({
                next: (phone) => {
                  const phones = store.phones();
                  const updatedPhones = phones ? phones.map(p => p.PhoneId === id ? phone : p) : [phone];

                  patchState(store, { phones: updatedPhones, ...setPropLoaded('loadPhone') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_LOAD' });
                  patchState(store, { ...setPropError('loadPhone', error) });
                },
              })
            )
          )
        )
      ),

      createPhone: rxMethod<TPhone>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('createPhone') })),
          switchMap((phone) => {
            const phoneData = {
              ...phone,
              PersonId: store.personId()
            };

            return profileService.createPhone(phoneData).pipe(
              tapResponse({
                next: (createdPhone) => {
                  alertStore.create({
                    message: 'Telefone adicionado com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const phones = store.phones() || [];
                  patchState(store, {
                    phones: [...phones, createdPhone],
                    ...setPropLoaded('createPhone')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_ADD' });
                  patchState(store, { ...setPropError('createPhone', error) });
                },
              })
            );
          })
        )
      ),

      updatePhone: rxMethod<TPhone>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updatePhone') })),
          switchMap((phone) => {
            const phoneData = {
              ...phone,
              PersonId: store.personId()
            };

            return profileService.updatePhone(phoneData).pipe(
              tapResponse({
                next: (updatedPhone) => {
                  alertStore.create({
                    message: 'Telefone atualizado com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const phones = store.phones();
                  const updatedPhones = phones ? phones.map(p => p.PhoneId === phone.PhoneId ? updatedPhone : p) : [updatedPhone];

                  patchState(store, {
                    phones: updatedPhones,
                    ...setPropLoaded('updatePhone')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_EDIT' });
                  patchState(store, { ...setPropError('updatePhone', error) });
                },
              })
            );
          })
        )
      ),

      patchPhone: rxMethod<Partial<TPhone>>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('patchPhone') })),
          switchMap((propertyPatch) => {
            const patchData = {
              ...propertyPatch,
              PersonId: store.personId()
            };

            return profileService.patchPhone(patchData).pipe(
              tapResponse({
                next: (updatedPhone) => {
                  const phones = store.phones();
                  const updatedPhones = phones ? phones.map(p =>
                    p.PhoneId === propertyPatch.PhoneId ? updatedPhone : p) : [updatedPhone];

                  patchState(store, {
                    phones: updatedPhones,
                    ...setPropLoaded('patchPhone')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_EDIT' });
                  patchState(store, { ...setPropError('patchPhone', error) });
                },
              })
            );
          })
        )
      ),

      deletePhone: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('deletePhone') })),
          switchMap((id) =>
            profileService.deletePhone(id).pipe(
              tapResponse({
                next: () => {
                  alertStore.create({
                    message: 'Telefone removido com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const phones = store.phones();
                  const updatedPhones = phones ? phones.filter(p => p.PhoneId !== id) : [];

                  patchState(store, {
                    phones: updatedPhones,
                    ...setPropLoaded('deletePhone')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.PHONES.ERROR_PHONE_DELETE' });
                  patchState(store, { ...setPropError('deletePhone', error) });
                },
              })
            )
          )
        )
      ),


      loadAddresses: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadAddresses') })),
          switchMap(() =>
            profileService.getAddressByPerson(store.personId()).pipe(
              tapResponse({
                next: (addresses) => {
                  patchState(store, { addresses, ...setPropLoaded('loadAddresses') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESSES_LOAD' });
                  patchState(store, { ...setPropError('loadAddresses', error) });
                },
              })
            )
          )
        )
      ),

      loadAddress: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('loadAddress') })),
          switchMap((id) =>
            profileService.getAddress(id).pipe(
              tapResponse({
                next: (address) => {
                  const addresses = store.addresses();
                  const updatedAddresses = addresses ? addresses.map(a =>
                    a.AddressId === id ? address : a) : [address];

                  patchState(store, { addresses: updatedAddresses, ...setPropLoaded('loadAddress') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_LOAD' });
                  patchState(store, { ...setPropError('loadAddress', error) });
                },
              })
            )
          )
        )
      ),

      createAddress: rxMethod<TAddress>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('createAddress') })),
          switchMap((address) => {
            const addressData = {
              ...address,
              PersonId: store.personId()
            };

            return profileService.createAddress(addressData).pipe(
              tapResponse({
                next: (createdAddress) => {
                  alertStore.create({
                    message: 'Endereço adicionado com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const addresses = store.addresses() || [];
                  patchState(store, {
                    addresses: [...addresses, createdAddress],
                    ...setPropLoaded('createAddress')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_ADD' });
                  patchState(store, { ...setPropError('createAddress', error) });
                },
              })
            );
          })
        )
      ),

      updateAddress: rxMethod<TAddress>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('updateAddress') })),
          switchMap((address) => {
            const addressData = {
              ...address,
              PersonId: store.personId()
            };

            return profileService.updateAddress(addressData).pipe(
              tapResponse({
                next: (updatedAddress) => {
                  alertStore.create({
                    message: 'Endereço atualizado com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const addresses = store.addresses();
                  const updatedAddresses = addresses ? addresses.map(a =>
                    a.AddressId === address.AddressId ? updatedAddress : a) : [updatedAddress];

                  patchState(store, {
                    addresses: updatedAddresses,
                    ...setPropLoaded('updateAddress')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_EDIT' });
                  patchState(store, { ...setPropError('updateAddress', error) });
                },
              })
            );
          })
        )
      ),

      patchAddress: rxMethod<Partial<TAddress>>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('patchAddress') })),
          switchMap((propertyPatch) => {
            const patchData = {
              ...propertyPatch,
              PersonId: store.personId()
            };

            return profileService.patchAddress(patchData).pipe(
              tapResponse({
                next: (updatedAddress) => {
                  const addresses = store.addresses();
                  const updatedAddresses = addresses ? addresses.map(a =>
                    a.AddressId === propertyPatch.AddressId ? updatedAddress : a) : [updatedAddress];

                  patchState(store, {
                    addresses: updatedAddresses,
                    ...setPropLoaded('patchAddress')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_EDIT' });
                  patchState(store, { ...setPropError('patchAddress', error) });
                },
              })
            );
          })
        )
      ),

      deleteAddress: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('deleteAddress') })),
          switchMap((id) =>
            profileService.deleteAddress(id).pipe(
              tapResponse({
                next: () => {
                  alertStore.create({
                    message: 'Endereço removido com sucesso!',
                    type: 'success',
                    title: 'COMPONENTS.ALERT.SUCCESS'
                  });

                  const addresses = store.addresses();
                  const updatedAddresses = addresses ? addresses.filter(a => a.AddressId !== id) : [];

                  patchState(store, {
                    addresses: updatedAddresses,
                    ...setPropLoaded('deleteAddress')
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: 'PROFILE.ALERT.ADDRESSES.ERROR_ADDRESS_DELETE' });
                  patchState(store, { ...setPropError('deleteAddress', error) });
                },
              })
            )
          )
        )
      ),

      getAccessFrequency: rxMethod<{ startOn: string, endOn: string }>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getAccessFrequency') })),
        switchMap(({ startOn, endOn }) => profileService.getAccessFrequency(store.personId(), startOn, endOn)
          .pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { AccessFrequency: response?.value || [], ...setPropLoaded('getAccessFrequency') });
              },
              error: (error) => {
                patchState(store, { ...setPropError('getAccessFrequency', error) })
              }
            })
          ))
      )),

      getTotalReceivementDailySummaryByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getTotalReceivementDailySummaryByPersonId') })),
        switchMap((personId) =>
          profileService.getTotalReceivementDailySummaryByPersonId(personId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { receivementSummary: response, ...setPropLoaded('getTotalReceivementDailySummaryByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { receivementSummary: null, ...setPropError('getTotalReceivementDailySummaryByPersonId', error) });
              }
            })
          )
        )
      )),

      getTotalAccountsReceivablesSummaryByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getTotalAccountsReceivablesSummaryByPersonId') })),
        switchMap((personId) =>
          profileService.getTotalAccountsReceivablesSummaryByPersonId(personId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { accountsReceivablesSummary: response, ...setPropLoaded('getTotalAccountsReceivablesSummaryByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { accountsReceivablesSummary: null, ...setPropError('getTotalAccountsReceivablesSummaryByPersonId', error) });
              }
            })
          )
        )
      )),

      getTotalOpenMonthByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getTotalOpenMonthByPersonId') })),
        switchMap((personId) =>
          profileService.getTotalOpenMonthByPersonId(personId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { openMonthByPerson: response, ...setPropLoaded('getTotalOpenMonthByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { openMonthByPerson: null, ...setPropError('getTotalOpenMonthByPersonId', error) });
              }
            })
          )
        )
      )),

      getTimelineByPeriod: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getTimelineByPeriod') })),
        switchMap((yearMonth) =>
          profileService.getTimelineByPeriod(store.personId(), yearMonth).pipe(
            tapResponse({
              next: ({ List, NextDate }) => {
                const orderedList = List.sort((a, b) => DateTime.fromISO(a.EventOn) < DateTime.fromISO(b.EventOn) ? 1 : -1);
                const TimelineEvents = [...store.TimelineEvents(), ...orderedList];
                patchState(store, {
                  TimelineNextDate: NextDate,
                  TimelineEvents,
                  ...setPropLoaded('getTimelineByPeriod')
                });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getTimelineByPeriod', error) });
              },
            })
          ))
      )),

      toggleGraduationModal: (showGraduationModal = !store.showGraduationModal(), graduation: Partial<TGraduation> = null) => {
        patchState(store, { showGraduationModal, graduation });
      },

      getAllGraduationsByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getAllGraduationsByPersonId') })),
        switchMap((personId) =>
          profileService.getAllGraduationsByPersonId(personId).pipe(
            tapResponse({
              next: (graduations) => {
                graduations.sort((a, b) => DateTime.fromISO(b.GraduationOn).toMillis() - DateTime.fromISO(a.GraduationOn).toMillis());
                for (const graduation of graduations) {
                  graduation?.Level?.SubLevels?.sort((a, b) => a.SubLevelOrder - b.SubLevelOrder);
                }
                patchState(store, { graduations }, setPropLoaded('getAllGraduationsByPersonId'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getAllGraduationsByPersonId', error) });
              }
            })
          )
        )
      )),

      deleteGraduationById,

      openDialogDeleteGraduation: (graduationId: string, deleteGraduationLoading: Signal<boolean>, deleteGraduationLoaded: Signal<boolean>, deleteGraduationError: Signal<boolean>): void => {
        dialogStore.openDialog({
          title: 'PROFILE.GRADUATION.DIALOG.TITLE',
          message: 'PROFILE.GRADUATION.DIALOG.MESSAGE',
          closeOnSignal: [deleteGraduationLoaded, deleteGraduationError],
          secondaryButton: {
            label: 'COMPONENTS.BUTTON.YES_DELETE',
            type: 'destructive',
            loadingOnsignal: deleteGraduationLoading,
            action: () => {
              deleteGraduationById(graduationId);
            },
          },
          primaryButton: {
            label: 'COMPONENTS.BUTTON.CANCEL',
            action: () => {
              dialogStore.closeDialog();
            },
          },
        })
      },

      createGraduation: rxMethod<Omit<TGraduation, 'PersonActivityLevelId'>>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createGraduation') })),
        switchMap((payload) =>
          profileService.createGraduation(payload).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { graduation: response, ...setPropLoaded('createGraduation') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('createGraduation', error) });
              },
            })
          )
        )
      )),

      updateGraduation: rxMethod<TGraduation>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateGraduation') })),
        switchMap((payload) =>
          profileService.updateGraduation(payload).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { graduation: response, ...setPropLoaded('updateGraduation') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('updateGraduation', error) });
              },
            })
          )
        )
      )),

      getContractInfoByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getContractInfoByPersonId') })),
        switchMap((personId) =>
          profileService.getContractInfoByPersonId(personId).pipe(
            tapResponse({
              next: (contractInfo) => {
                patchState(store, { contractInfo, ...setPropLoaded('getContractInfoByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getContractInfoByPersonId', error) });
              }
            })
          )
        )
      )),

      copyToPaguela: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('copyToPaguela') })),
        switchMap((id) => profileService.copyToPaguela(id).pipe(
          tapResponse({
            next: (paguelaProfile) => {
              const profile = structuredClone(store.profile());
              profile.Integration = {
                ...(profile.Integration || {}),
                PagueLaId: paguelaProfile?.PersonId,
              }
              patchState(store, { profile, ...setPropLoaded('copyToPaguela') });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, { ...setPropError('copyToPaguela', error) });
            }
          })
        ))
      )),

      generateSimpleLoginCode: rxMethod<string>(pipe(
        filter((personId): personId is string => personId != null && personId !== ''),
        tap(() => patchState(store, { ...setPropLoading('generateSimpleLoginCode') })),
        switchMap((personId) =>
          profileService.generateSimpleLoginCode(personId).pipe(
            tapResponse({
              next: (simpleLoginCode) => {
                patchState(store, { simpleLoginCode, ...setPropLoaded('generateSimpleLoginCode') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('generateSimpleLoginCode', error) });
              },
            })
          )
        )
      )),

      getPersonCategoryHistoryByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getPersonCategoryHistoryByPersonId') })),
        switchMap((personId) =>
          profileService.getPersonCategoryHistoryByPersonId(personId).pipe(
            tapResponse({
              next: (personCategoryHistory) => {
                patchState(store, { personCategoryHistory, ...setPropLoaded('getPersonCategoryHistoryByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getPersonCategoryHistoryByPersonId', error) });
              },
            })
          )
        )
      )),

      getPersonCategoryByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getPersonCategoryByPersonId') })),
        switchMap((personId) =>
          profileService.getPersonCategoryByPersonId(personId).pipe(
            tapResponse({
              next: (personCategory) => {
                patchState(store, { personCategory, ...setPropLoaded('getPersonCategoryByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getPersonCategoryByPersonId', error) });
              },
            })
          )
        )
      )),

      getAllCreditAssessmentPJ: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getAllCreditAssessmentPJ') })),
        switchMap((companyFiscalIdentity) =>
          profileService.getAllCreditAssessmentPJ(companyFiscalIdentity).pipe(
            tapResponse({
              next: (allCreditAssessmentPJ) => {
                patchState(store, { allCreditAssessmentPJ, ...setPropLoaded('getAllCreditAssessmentPJ') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getAllCreditAssessmentPJ', error) });
              },
            })
          )
        )
      )),

      getSerasaPJById: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getSerasaPJById') })),
        switchMap((id) =>
          profileService.getSerasaPJById(id).pipe(
            tapResponse({
              next: (creditAssessmentPJ) => {
                patchState(store, { creditAssessmentPJ, ...setPropLoaded('getSerasaPJById') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getSerasaPJById', error) });
              },
            })
          )
        )
      )),

      getCreditAssessmentPJ: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getCreditAssessmentPJ') })),
        switchMap((companyFiscalIdentity) =>
          profileService.getCreditAssessmentPJ(companyFiscalIdentity).pipe(
            tapResponse({
              next: (creditAssessmentPJ) => {
                patchState(store, { creditAssessmentPJ, ...setPropLoaded('getCreditAssessmentPJ') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getCreditAssessmentPJ', error) });
              },
            })
          )
        )
      )),

      getLastCreditAssessmentPJ: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getLastCreditAssessmentPJ') })),
        switchMap((companyFiscalIdentity) =>
          profileService.getLastCreditAssessmentPJ(companyFiscalIdentity).pipe(
            tapResponse({
              next: (creditAssessmentPJ) => {
                patchState(store, { creditAssessmentPJ, ...setPropLoaded('getLastCreditAssessmentPJ') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getLastCreditAssessmentPJ', error) });
              },
            })
          )
        )
      )),

      getAllCreditAssessmentPF: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getAllCreditAssessmentPF') })),
        switchMap((fiscalIdentity) =>
          profileService.getAllCreditAssessmentPF(fiscalIdentity).pipe(
            tapResponse({
              next: (allCreditAssessmentPF) => {
                patchState(store, { allCreditAssessmentPF, ...setPropLoaded('getAllCreditAssessmentPF') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getAllCreditAssessmentPF', error) });
              },
            })
          )
        )
      )),

      getSerasaPFById: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getSerasaPFById') })),
        switchMap((id) =>
          profileService.getSerasaPFById(id).pipe(
            tapResponse({
              next: (creditAssessmentPF) => {
                patchState(store, { creditAssessmentPF, ...setPropLoaded('getSerasaPFById') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getSerasaPFById', error) });
              },
            })
          )
        )
      )),

      getCreditAssessmentPF: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getCreditAssessmentPF') })),
        switchMap((fiscalIdentity) =>
          profileService.getCreditAssessmentPF(fiscalIdentity).pipe(
            tapResponse({
              next: (creditAssessmentPF) => {
                patchState(store, { creditAssessmentPF, ...setPropLoaded('getCreditAssessmentPF') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getCreditAssessmentPF', error) });
              },
            })
          )
        )
      )),

      getLastCreditAssessmentPF: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getLastCreditAssessmentPF') })),
        switchMap((fiscalIdentity) =>
          profileService.getLastCreditAssessmentPF(fiscalIdentity).pipe(
            tapResponse({
              next: (creditAssessmentPF) => {
                patchState(store, { creditAssessmentPF, ...setPropLoaded('getLastCreditAssessmentPF') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getLastCreditAssessmentPF', error) });
              },
            })
          )
        )
      )),

      setPropsInit(props: string[]): void {
        patchState(store, setPropInit(...props));
      },

      blankAssessmentCredit: () => {
        patchState(store, {
          creditAssessmentPF: null,
          creditAssessmentPJ: null,
          allCreditAssessmentPF: null,
          allCreditAssessmentPJ: null,
        }, setPropInit(
          'getAllCreditAssessmentPJ',
          'getSerasaPJById',
          'getCreditAssessmentPJ',
          'getLastCreditAssessmentPJ',
          'getAllCreditAssessmentPF',
          'getSerasaPFById',
          'getCreditAssessmentPF',
          'getLastCreditAssessmentPF'
        ))
      },

      getIntegration: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getIntegration') })),
        switchMap((id) =>
          profileService.getIntegration(id).pipe(
            tapResponse({
              next: (integration) => {
                patchState(store, { integration, ...setPropLoaded('getIntegration') });
              },
              error: (error: HttpErrorResponse) => {
                patchState(store, { ...setPropError('getIntegration', error) });
              },
            })
          )
        )
      )),

      updateIntegration: rxMethod<TPersonIntegration>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateIntegration') })),
        switchMap((data) => {
          if (!data.TotalpassId) return of(data);
          return profileService.checkTotalPassKey(data.TotalpassId).pipe(
            map(() => data),
            tapResponse({
              next: () => { },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('updateIntegration', error) });
              },
            })
          )
        }),
        switchMap((data) =>
          profileService.updateIntegration(data).pipe(
            tapResponse({
              next: (integration) => {
                alertStore.create({
                  message: 'PROFILE.INTEGRATION_UPDATED_SUCCESSFULLY',
                  type: 'success',
                  title: 'COMPONENTS.ALERT.SUCCESS'
                });
                patchState(store, { integration, ...setPropLoaded('updateIntegration') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('updateIntegration', error) });
              },
            })
          )
        )
      )),

      getProfileProducts: rxMethod<string>(pipe(
        tap(() => patchState(store, setPropLoading('getProfileProducts'))),
        switchMap((personId) =>
          profileService.getPersonProducts(personId).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { profileProducts: response }, setPropLoaded('getProfileProducts'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getProfileProducts', error) });
              }
            })
          )
        )
      )),

      resetTimeline: () => {
        patchState(store, {
          TimelineEvents: [],
          TimelineNextDate: null,
          ...setPropInit('getTimelineByPeriod')
        })
      },

      getPaguelaAccount: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getPaguelaAccount') })),
        switchMap((id) =>
          profileService.getPaguelaAccount(id).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { paguelaAccount: response, ...setPropLoaded('getPaguelaAccount') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getPaguelaAccount', error) });
              },
            })
          )
        )
      )),

      updatePaguelaSituation: rxMethod<{ id: string, situation: EPaguelaSituation }>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updatePaguelaSituation') })),
        switchMap(({ id, situation }) =>
          profileService.updatePaguelaSituation(id, situation).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { paguelaAccount: response, ...setPropLoaded('updatePaguelaSituation') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('updatePaguelaSituation', error) });
              },
            })
          )
        )
      )),

      getActivities: rxMethod<void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getActivities') })),
        switchMap(() =>
          profileService.getAllActivities().pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { activities: response?.value, ...setPropLoaded('getActivities') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getActivities', error) });
              },
            })
          )
        )
      )),

      getActivitiesByPersonId: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getActivitiesByPersonId') })),
        switchMap((id) =>
          profileService.getActivitiesByPersonId(id).pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { profileActivities: response?.value, ...setPropLoaded('getActivitiesByPersonId') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('getActivitiesByPersonId', error) });
              },
            })
          )
        )
      )),

      blankActivities: () => {
        patchState(store, { profileActivities: [], ...setPropInit('getActivitiesByPersonId') });
      },

      blankLoginCode: () => {
        patchState(store, { simpleLoginCode: null }, setPropInit('generateSimpleLoginCode'));
      },

      blankPaguelaState: () => {
        patchState(store, { paguelaAccount: null }, setPropInit('getPaguelaAccount', 'updatePaguelaSituation'));
      },
    };
  }),
  withReqState(
    'getProfile',
    'updatePerson',
    'createPerson',
    'verifyEmail',
    'isEmailAlreadyUsed',
    'checkIsPossibleToSetTemporaryEmail',
    'checkAccessCdExists',
    'checkCpfExists',
    'checkFullNameExists',
    'checkCompany',
    'reVerifyEmail',
    'toggleRead',
    'addComment',
    'updateComment',
    'removeComment',
    'getLastComment',
    'getTotalReceivementDailySummaryByPersonId',
    'getTotalAccountsReceivablesSummaryByPersonId',
    'patchPerson',
    'getResponsible',
    'createResponsible',
    'updateResponsible',
    'patchResponsible',
    'deleteResponsible',
    'loadPhones',
    'loadPhone',
    'createPhone',
    'updatePhone',
    'patchPhone',
    'deletePhone',
    'loadAddresses',
    'loadAddress',
    'createAddress',
    'updateAddress',
    'patchAddress',
    'deleteAddress',
    'getAccessFrequency',
    'getTotalOpenMonthByPersonId',
    'getComment',
    'profileEdit',
    'getTimelineByPeriod',
    'deleteGraduationById',
    'getAllGraduationsByPersonId',
    'createGraduation',
    'updateGraduation',
    'getGroupByPersonId',
    'getContractInfoByPersonId',
    'copyToPaguela',
    'generateSimpleLoginCode',
    'getPersonCategoryHistoryByPersonId',
    'getPersonCategoryByPersonId',
    'getAllCreditAssessmentPJ',
    'getSerasaPJById',
    'getCreditAssessmentPJ',
    'getLastCreditAssessmentPJ',
    'getAllCreditAssessmentPF',
    'getSerasaPFById',
    'getCreditAssessmentPF',
    'getLastCreditAssessmentPF',
    'getIntegration',
    'updateIntegration',
    'getProfileProducts',
    'getPaguelaAccount',
    'updatePaguelaSituation',
    'getActivitiesByPersonId',
    'getActivities'
  )
);
